import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationsService } from '@core/services/notifications.service';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@shared/icon/icon.component';
import { INotificationEvento, INotificationResponse } from '@models/INotification';

@Component({
  selector: 'app-notifications',
  imports: [
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    CommonModule,
    TranslateModule,
    IconComponent,
    MatExpansionPanelTitle
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent {
  private readonly destroyRef = inject(DestroyRef);

  notifications = signal<INotificationEvento[]>([]);
  translatedNotifications = signal<string[]>([]);
  totalNotifications = signal<number>(0);
  summaryMessage = signal('');
  readonly panelOpenState = signal(false);

  @HostBinding('style.display')
  get display() {
    return this.totalNotifications() > 0 ? 'block' : 'none';
  }


  constructor(
    private notificationsService: NotificationsService,
    private translateService: TranslateService,
  ) {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshTranslations();
      });

    this.notificationsService.getHomeNotifications().subscribe({
      next: (r: INotificationResponse) => {
        this.notifications.set(r.items);
        this.totalNotifications.set(r.total);
        this.refreshTranslations();
      },
      error: (err) => {

        console.warn('Error en notificaciones:', err.message);

      },
    });
  }

  private refreshTranslations() {
    this.refreshSummaryMessage();
    this.refreshNotificationMessages();
  }

  private refreshSummaryMessage() {
    const total = this.totalNotifications();
    const summaryKey =
      total === 1
        ? 'NOTIFICACIONES.MESSAGE'
        : 'NOTIFICACIONES.MESSAGE_PLURAL';

    this.translateService
      .get(summaryKey, { count: total })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translation) => {
        this.summaryMessage.set(translation);
      });
  }

  private refreshNotificationMessages() {
    const notifications = this.notifications();

    if (!notifications.length) {
      this.translatedNotifications.set([]);
      return;
    }

    const translationKeys = notifications.map(
      (notification) => `${notification.titleKey}_home`
    );

    this.translateService
      .get(translationKeys)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((translations) => {
        this.translatedNotifications.set(
          notifications.map((notification) =>
            this.translateService.parser.interpolate(
              translations[`${notification.titleKey}_home`],
              notification.titleParams
            ) ?? `${notification.titleKey}_home`
          )
        );
      });
  }
}
